import { useMemo, useState } from "react";
import { Receipt } from "lucide-react";
import { ToolLayout, TwoCol } from "@/components/tools/ToolLayout";
import { SectionCard, Field, MoneyInput, Segmented, StatCard, InfoRow, compactINR, inr } from "@/components/tools/kit";
import { ExportButtons } from "@/components/tools/ExportButtons";
import { ToolAdvisor } from "@/components/tools/ToolAdvisor";
import { incomeTax } from "@/lib/calculators";

export default function IncomeTax() {
  const [grossIncome, setGross] = useState(1500000);
  const [salaried, setSalaried] = useState("yes");
  const [ded80C, set80C] = useState(150000);
  const [ded80D, set80D] = useState(25000);
  const [hra, setHra] = useState(0);
  const [homeLoanInterest, setHLI] = useState(0);
  const [nps80CCD, setNps] = useState(0);
  const isSalaried = salaried === "yes";
  const r = useMemo(() => incomeTax({ grossIncome, ded80C, ded80D, hra, homeLoanInterest, nps80CCD, isSalaried }),
    [grossIncome, ded80C, ded80D, hra, homeLoanInterest, nps80CCD, isSalaried]);

  const buildPayload = () => ({
    title: "My Finanz Planner Income Tax Report",
    subtitle: "FY 2025-26 · Old vs New regime",
    meta: [
      { label: "Gross income", value: inr(grossIncome) },
      { label: "New regime tax", value: inr(r.taxNew) },
      { label: "Old regime tax", value: inr(r.taxOld) },
      { label: "Better regime", value: r.better === "new" ? "New" : "Old" },
      { label: "You save", value: inr(r.savings) },
    ],
    sheets: [{
      name: "Comparison", headers: ["Metric", "New Regime", "Old Regime"],
      rows: [
        ["Taxable income", Math.round(r.taxableNew), Math.round(r.taxableOld)],
        ["Total tax (incl. 4% cess)", r.taxNew, r.taxOld],
      ],
    }],
  });

  const newBetter = r.better === "new";
  return (
    <ToolLayout title="Income Tax Calculator" subtitle="Old vs New regime for FY 2025-26 (AY 2026-27)" icon={Receipt} accent="#D99B26">
      <TwoCol
        left={
          <SectionCard title="Your income & deductions">
            <div className="space-y-5">
              <Field label="Gross annual income"><MoneyInput testid="tax-gross" value={grossIncome} onChange={setGross} helper={compactINR(grossIncome)} /></Field>
              <Field label="Salaried / Pensioner?">
                <Segmented testid="tax-salaried" value={salaried} onChange={setSalaried} layoutId="tax-sal-pill"
                  options={[{ id: "yes", label: "Yes" }, { id: "no", label: "No" }]} />
              </Field>
              <p className="text-xs font-semibold text-[#8A9790] uppercase tracking-wide pt-1">Old-regime deductions</p>
              <Field label="80C (EPF, ELSS, PPF…) cap ₹1.5L"><MoneyInput testid="tax-80c" value={ded80C} onChange={set80C} /></Field>
              <Field label="80D (health insurance)"><MoneyInput testid="tax-80d" value={ded80D} onChange={set80D} /></Field>
              <Field label="HRA exemption"><MoneyInput testid="tax-hra" value={hra} onChange={setHra} /></Field>
              <Field label="Home loan interest (24b) cap ₹2L"><MoneyInput testid="tax-hli" value={homeLoanInterest} onChange={setHLI} /></Field>
              <Field label="NPS 80CCD(1B) cap ₹50k"><MoneyInput testid="tax-nps" value={nps80CCD} onChange={setNps} /></Field>
            </div>
          </SectionCard>
        }
        right={
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className={`rounded-2xl p-5 text-white shadow-sm ${newBetter ? "ring-4 ring-[#2D6A4F]/30 bg-[#2D6A4F]" : "bg-[#2A6F7F]"}`} data-testid="tax-new">
                <div className="text-[11px] uppercase tracking-wider font-semibold text-white/70">New Regime {newBetter && "· Better"}</div>
                <div className="font-heading text-3xl font-extrabold mt-1">{compactINR(r.taxNew)}</div>
                <div className="text-xs text-white/70 mt-1">Taxable {compactINR(r.taxableNew)}</div>
              </div>
              <div className={`rounded-2xl p-5 text-white shadow-sm ${!newBetter ? "ring-4 ring-[#2D6A4F]/30 bg-[#2D6A4F]" : "bg-[#D99B26]"}`} data-testid="tax-old">
                <div className="text-[11px] uppercase tracking-wider font-semibold text-white/70">Old Regime {!newBetter && "· Better"}</div>
                <div className="font-heading text-3xl font-extrabold mt-1">{compactINR(r.taxOld)}</div>
                <div className="text-xs text-white/70 mt-1">Taxable {compactINR(r.taxableOld)}</div>
              </div>
            </div>
            <SectionCard title="Verdict">
              <InfoRow label="Recommended regime" value={newBetter ? "New Regime" : "Old Regime"} strong />
              <InfoRow label="You save by choosing it" value={compactINR(r.savings)} strong />
              <InfoRow label="Total old-regime deductions" value={compactINR(r.totalDedOld)} />
              <p className="text-xs text-[#8A9790] mt-3">New regime: ₹75k standard deduction, no tax up to ₹12L taxable (Sec 87A). Figures include 4% cess. Surcharge for very high incomes not modelled.</p>
            </SectionCard>
            <ToolAdvisor tool="income-tax" title="Income Tax (FY 2025-26)"
              context={[
                { label: "Gross income", value: compactINR(grossIncome) },
                { label: "New regime tax", value: compactINR(r.taxNew) },
                { label: "Old regime tax", value: compactINR(r.taxOld) },
                { label: "Total deductions (old)", value: compactINR(r.totalDedOld) },
                { label: "Better regime", value: newBetter ? "New" : "Old" },
              ]} />
            <ExportButtons buildPayload={buildPayload} />
          </>
        }
      />
    </ToolLayout>
  );
}
